/**
 * 管理画面の表示言語 (admin-english-toggle-20260924 / ADR-081) の型。
 *
 * - 日本語 (`ja`) が既定かつ正本。英語 (`en`) は利用者ごとに選べる表示言語。
 * - 辞書は `ja` を正本として書き、`en` は {@link TranslationOf} で `ja` と同じ形を強制する
 *   (キーの欠落・余分なキーは TypeScript のエラーになる)。
 * - 依頼者・案件のデータ (氏名・書類名・メッセージ本文など) は翻訳しない。訳すのは画面の枠 (ラベル) だけ。
 */

/** 対応している表示言語。 */
export type Locale = 'ja' | 'en';

/** 既定の表示言語 (正本)。辞書に無いキーもこの言語へ倒す。 */
export const DEFAULT_LOCALE: Locale = 'ja';

/** 対応している表示言語の一覧 (表示順)。 */
export const SUPPORTED_LOCALES: readonly Locale[] = ['ja', 'en'];

/** 任意の値が対応している表示言語か。localStorage やサーバーの値を読むときに使う。 */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * 辞書の木。葉は文言 (文字列)、節は名前空間。
 * 実行時の探索 (`lookupMessage`) はこの形だけを前提にする。
 */
export interface DictionaryTree {
  readonly [key: string]: string | DictionaryTree;
}

/**
 * `ja` の辞書と同じ形で、葉を任意の文字列にした型。`en` の辞書はこの型で宣言する。
 * object literal を代入するため、キーの欠落も余分なキーもコンパイルエラーになる。
 */
export type TranslationOf<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : TranslationOf<T[K]>;
};

/**
 * 辞書の葉のキー (ドット区切り)。例: `page.title` / `filters.status.all`。
 * 形の決まっていない辞書 (`DictionaryTree` の index signature。generic な handle の制約など) では
 * `string` に倒す — 再帰が終わらず型検査が止まらなくなるのを防ぐ。
 */
export type LeafKey<T> = string extends keyof T
  ? string
  : {
      [K in keyof T & string]: T[K] extends string ? K : `${K}.${LeafKey<T[K]>}`;
    }[keyof T & string];

/** 文言の差し込み値。`{name}` の形で文言に書く。 */
export type TranslationParams = Readonly<Record<string, string | number>>;

/**
 * 文言 (日本語辞書の文字列リテラル型) に含まれる `{name}` の名前の集合。無ければ never。
 * 例: `'{count} 件のタスク'` → `'count'`。
 */
export type PlaceholderNames<S> = S extends `${string}{${infer Name}}${infer Rest}`
  ? Name | PlaceholderNames<Rest>
  : never;

/** 辞書 T のドット区切りキー K の文言の型。K が union なら文言も union になる。 */
export type MessageAt<T, K extends string> = K extends `${infer Head}.${infer Tail}`
  ? Head extends keyof T
    ? MessageAt<T[Head], Tail>
    : never
  : K extends keyof T
    ? T[K]
    : never;

/**
 * 文言に合わせた差し込み値の引数。
 * - placeholder が無い文言: 省略する (渡すとコンパイルエラー)
 * - ある文言: 必須。名前の不足・綴りの誤り・余分な名前はコンパイルエラー
 */
export type ParamsArgs<S> = [PlaceholderNames<S>] extends [never]
  ? [params?: undefined]
  : [params: Readonly<Record<PlaceholderNames<S>, string | number>>];

/**
 * 名前空間の handle (`defineNamespace` が返す)。画面は `useT(tasksNs)` のように handle を渡す。
 * `ja` は型 (キー・差し込み値) を導くためだけに持つ。実行時の探索は registry から名前で引く。
 */
export interface NamespaceHandle<Ja extends DictionaryTree = DictionaryTree> {
  readonly name: string;
  readonly ja: Ja;
}

/** handle の日本語辞書 (正本) の型。 */
export type HandleDictionary<H> = H extends NamespaceHandle<infer Ja> ? Ja : never;

/** handle の名前空間内の文言キー (ドット区切り)。存在しないキーは型エラーになる。 */
export type HandleKey<H extends NamespaceHandle> = LeafKey<HandleDictionary<H>>;

/**
 * handle のキー K に渡す差し込み値の引数 (日本語辞書の文言の `{name}` から導く)。
 * placeholder の無い文言には渡せず、ある文言では名前の不足・綴りの誤り・余分がコンパイルエラーになる。
 */
export type HandleParamsArgs<H extends NamespaceHandle, K extends HandleKey<H>> = ParamsArgs<
  MessageAt<HandleDictionary<H>, K>
>;

/**
 * handle の名前空間のうち、差し込み値の無い (`{name}` を含まない) 文言のキー。
 * 区分値 → キーの対応表など「動的に選んだキーを値なしで引く」ところの型に使う。
 */
export type PlainHandleKey<H extends NamespaceHandle> = {
  [K in HandleKey<H>]: [PlaceholderNames<MessageAt<HandleDictionary<H>, K>>] extends [never]
    ? K
    : never;
}[HandleKey<H>];
